import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronDownIcon, CpuChipIcon } from '@heroicons/react/24/outline'
import { TAXONOMY } from '../nav'

interface SchoolOption { id: string; name: string }

/**
 * School picker — HOME is the only place a workspace/school is chosen. The choice
 * (id + name) is persisted to localStorage; cluster shells only display it read-only.
 */
function SchoolSelector() {
  const [schools] = useState<SchoolOption[]>(() => {
    try { return JSON.parse(localStorage.getItem('fc_schools') || '[]') } catch { return [] }
  })
  const [selected, setSelected] = useState(() => localStorage.getItem('fc_school_id') || '')
  if (schools.length < 1) return null
  return (
    <label className="flex items-center gap-2">
      <span className="hidden text-xs font-medium text-muted-foreground sm:inline">School</span>
      <div className="relative">
        <select
          aria-label="School"
          value={selected}
          onChange={event => {
            const id = event.target.value
            const match = schools.find(school => school.id === id)
            localStorage.setItem('fc_school_id', id)
            if (match) localStorage.setItem('fc_school_name', match.name)
            setSelected(id)
          }}
          className="h-9 appearance-none rounded-lg border border-input bg-background pl-3 pr-9 text-sm font-medium outline-none focus:border-ring focus:ring-2 focus:ring-ring/20"
        >
          {schools.map(school => <option key={school.id} value={school.id}>{school.name}</option>)}
        </select>
        <ChevronDownIcon className="pointer-events-none absolute right-2.5 top-2.5 size-4 text-muted-foreground" />
      </div>
    </label>
  )
}

function currentUser(): { name?: string; email?: string; avatar?: string } | null {
  try {
    const raw = localStorage.getItem('fc_admin_auth')
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

/**
 * The portal HOME. Presents the entire admin as 3 big groups, each with its 4
 * clusters, so the user can jump straight to any cluster without a dense sidebar.
 */
export default function HomePage() {
  const user = currentUser()
  // Groups start collapsed; the user expands one to reveal its section cards.
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const toggle = (id: string) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }))
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-10 flex h-16 items-center gap-3 border-b bg-background/95 px-4 backdrop-blur sm:px-8">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
          <CpuChipIcon className="size-5" />
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold leading-tight">Conan Admin</p>
          <p className="truncate text-xs text-muted-foreground">Admin Home</p>
        </div>
        <div className="ml-auto flex items-center gap-4">
          <SchoolSelector />
          {user && (
            <div className="flex items-center gap-2">
              {user.avatar
                ? <img src={user.avatar} alt="" className="size-8 rounded-full object-cover" />
                : <div className="flex size-8 items-center justify-center rounded-full bg-muted text-sm font-semibold">{(user.name || user.email || '?').charAt(0).toUpperCase()}</div>}
              <div className="hidden min-w-0 sm:block">
                <p className="truncate text-sm font-medium leading-tight">{user.name || 'Admin'}</p>
                <p className="truncate text-xs text-muted-foreground">{user.email}</p>
              </div>
            </div>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-6xl space-y-4 px-4 py-8 sm:px-8">
        {TAXONOMY.map(group => {
          const GroupIcon = group.Icon
          const open = !!expanded[group.id]
          // One card per page (flattened from the group's clusters) — no nested lists.
          const cards = group.clusters.flatMap(cluster =>
            cluster.items.filter(item => !item.hidden).map(item => ({ item, cluster })),
          )
          return (
            <section key={group.id} className="overflow-hidden rounded-2xl border bg-card">
              <button
                type="button"
                onClick={() => toggle(group.id)}
                aria-expanded={open}
                className="flex w-full items-center gap-3 p-4 text-left transition-colors hover:bg-muted/50"
              >
                <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <GroupIcon className="size-6" />
                </span>
                <h2 className="flex-1 text-lg font-semibold tracking-tight">{group.label}</h2>
                <span className="hidden text-xs text-muted-foreground sm:inline">{cards.length} items</span>
                <ChevronDownIcon className={`size-5 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
              </button>
              {open && (
                <div className="grid grid-cols-1 gap-3 border-t p-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {cards.map(({ item, cluster }) => {
                    const ClusterIcon = cluster.Icon
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className="group flex items-center gap-3 rounded-xl border bg-background p-4 transition-all hover:border-primary/40 hover:shadow-md"
                      >
                        <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-foreground transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                          <ClusterIcon className="size-5" />
                        </span>
                        <div className="min-w-0">
                          <p className="truncate font-semibold group-hover:text-primary">{item.label}</p>
                          <p className="truncate text-xs text-muted-foreground">{cluster.label}</p>
                        </div>
                      </Link>
                    )
                  })}
                </div>
              )}
            </section>
          )
        })}
      </main>
    </div>
  )
}
